
// FIX: Import from `firebase` where `db` is exported, not `firebaseConfig`.
import { auth, db } from "../src/firebase";
import {
  createUserWithEmailAndPassword,
  // FIX: Import User type from Firebase Auth for type safety
  type User as FirebaseUser,
  updateProfile,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, increment, collection, getDocs, query, orderBy, limit, startAfter, DocumentData, QueryDocumentSnapshot, addDoc, deleteDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import type { User, ProPlan, GeneratedName, GenerationHistoryEntry, UserInputs, Redirect, PageContent, NavLink, SocialLinks } from '../types';
import { ADMIN_EMAILS } from '../constants';

// --- User Management (Firebase Auth + Firestore Profile) ---

export async function createUser(name: string, email: string, password: string): Promise<User> {
  const lowerCaseEmail = email.toLowerCase();
  
  // Step 1: Create user in Firebase Authentication
  const userCredential = await createUserWithEmailAndPassword(auth, lowerCaseEmail, password);
  await updateProfile(userCredential.user, { displayName: name });
  const { uid } = userCredential.user;

  // Step 2: Create user profile document in Firestore
  // FIX: Create a base user object and conditionally add admin/pro properties
  // to prevent sending `undefined` for the `plan` field, which causes a Firestore error.
  const userObject: Omit<User, 'generationHistory'> = {
    uid,
    name,
    email: lowerCaseEmail,
    role: 'user', // Default role
    createdAt: new Date().toISOString(),
    isPro: false, // Default pro status
    stats: {
      namesGenerated: 0,
      pdfsExported: 0,
      generationsUsed: 0,
    },
    favorites: [],
  };

  // Special handling for the admin user during sign-up
  if (ADMIN_EMAILS.includes(lowerCaseEmail)) {
    userObject.name = 'Super Admin';
    userObject.role = 'admin';
    userObject.isPro = true;
    userObject.plan = 'Yearly';
  }

  await setDoc(doc(db, "users", uid), userObject);
  
  const newUser: User = {
      ...userObject,
      generationHistory: []
  };
  
  return newUser;
}

/**
 * Creates a Firestore profile for a user who already exists in Firebase Authentication
 * but does not have a corresponding profile document. This is crucial for migrating
 * users from v1 to v2 seamlessly on their first login.
 */
export async function createProfileForExistingUser(firebaseUser: FirebaseUser): Promise<User> {
    const { uid, displayName, email } = firebaseUser;
    const lowerCaseEmail = email?.toLowerCase() || '';

    const userObject: Omit<User, 'generationHistory'> = {
        uid,
        name: displayName || 'New User',
        email: lowerCaseEmail,
        role: 'user',
        createdAt: new Date().toISOString(),
        isPro: false,
        stats: {
            namesGenerated: 0,
            pdfsExported: 0,
            generationsUsed: 0,
        },
        favorites: [],
    };

    // Special handling for the admin user on first login to v2
    if (ADMIN_EMAILS.includes(lowerCaseEmail)) {
        userObject.name = 'Super Admin';
        userObject.role = 'admin';
        userObject.isPro = true;
        userObject.plan = 'Yearly';
    }

    await setDoc(doc(db, "users", uid), userObject);
    
    const newProfile: User = {
        ...userObject,
        generationHistory: []
    }

    return newProfile;
}

export async function getUserProfile(uid: string): Promise<User | null> {
  const docRef = doc(db, "users", uid);
  const userDocSnap = await getDoc(docRef);

  if (userDocSnap.exists()) {
    const userData = userDocSnap.data() as Omit<User, 'generationHistory'>;

    // Fetch history from the subcollection
    const historyCollectionRef = collection(db, "users", uid, "generationHistory");
    const historyQuery = query(historyCollectionRef, orderBy("date", "desc"), limit(50)); // Limit to last 50 for performance
    const historySnapshot = await getDocs(historyQuery);
    const generationHistory = historySnapshot.docs.map(doc => doc.data() as GenerationHistoryEntry);
    
    return { uid, ...userData, generationHistory } as User;
  } else {
    console.error("No user profile found in Firestore for UID:", uid);
    return null;
  }
}

export async function getAllUsers(startAfterDoc?: QueryDocumentSnapshot<DocumentData>): Promise<{ users: User[], lastVisible: QueryDocumentSnapshot<DocumentData> | null }> {
  const usersCollection = collection(db, "users");
  
  const q = startAfterDoc 
      ? query(usersCollection, orderBy("createdAt", "desc"), startAfter(startAfterDoc), limit(20))
      : query(usersCollection, orderBy("createdAt", "desc"), limit(20));

  const userSnapshot = await getDocs(q);

  const usersList: User[] = userSnapshot.docs.map(doc => ({
      uid: doc.id,
      ...doc.data()
  } as User));
  
  const lastVisible = userSnapshot.docs[userSnapshot.docs.length - 1];

  return { users: usersList, lastVisible: lastVisible || null };
}

// --- User Profile Data Management ---

export async function updateUserProStatus(uid: string, isPro: boolean, plan?: ProPlan): Promise<User | null> {
    const userRef = doc(db, "users", uid);
    
    const updateData: any = { isPro };
    if (isPro && plan) {
      updateData.plan = plan;
    } else {
      updateData.plan = null;
    }

    await updateDoc(userRef, updateData);
    return await getUserProfile(uid);
}

export async function addGeneratedNamesToHistory(uid: string, names: GeneratedName[], inputs: UserInputs): Promise<void> {
    const historyCollectionRef = collection(db, "users", uid, "generationHistory");
    
    const newHistoryEntry: GenerationHistoryEntry = {
        names,
        date: new Date().toISOString(),
        inputs,
    };
    
    await setDoc(doc(historyCollectionRef), newHistoryEntry);
}

export async function updateUserStat(uid: string, stat: 'namesGenerated' | 'pdfsExported' | 'generationsUsed', incrementBy: number = 1): Promise<User | null> {
    const userRef = doc(db, "users", uid);
    
    // Use Firestore's atomic increment operation for safety and efficiency
    await updateDoc(userRef, {
        [`stats.${stat}`]: increment(incrementBy)
    });
    
    return await getUserProfile(uid);
}

export async function toggleFavoriteName(uid: string, name: GeneratedName, isCurrentlyFavorited: boolean): Promise<User | null> {
  const userRef = doc(db, "users", uid);
  await updateDoc(userRef, {
    favorites: isCurrentlyFavorited ? arrayRemove(name) : arrayUnion(name)
  });
  return await getUserProfile(uid);
}

export async function updateUserCustomLogo(uid: string, base64Logo: string | null): Promise<User | null> {
  const userRef = doc(db, "users", uid);
  await updateDoc(userRef, {
    customLogoBase64: base64Logo
  });
  return await getUserProfile(uid);
}

// --- Password Management ---

export async function changeUserPassword(currentPassword: string, newPassword: string): Promise<void> {
  const user = auth.currentUser;
  if (!user || !user.email) {
    throw new Error("No authenticated user found.");
  }
  
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  
  // Re-authenticate the user before changing the password for security
  await reauthenticateWithCredential(user, credential);
  
  // Now change the password
  await updatePassword(user, newPassword);
}

export async function requestPasswordReset(email: string): Promise<void> {
  const lowerCaseEmail = email.toLowerCase();
  try {
    await sendPasswordResetEmail(auth, lowerCaseEmail);
  } catch (error: any) {
    console.error("Error sending password reset email:", error);
    // Provide a more user-friendly error
    if (error.code === 'auth/user-not-found') {
      // To prevent email enumeration attacks, you might want to show a generic message.
      // However, for better UX in this context, we'll be specific.
      throw new Error("No account found with this email address.");
    }
    throw new Error(error.message || "Failed to send password reset email.");
  }
}

// --- Admin: Content & Site Management ---

export async function addPage(pageId: string, pageData: PageContent): Promise<void> {
  const docRef = doc(db, "appContent", pageId);
  await setDoc(docRef, pageData);
}

export async function deletePage(pageId: string): Promise<void> {
  const docRef = doc(db, "appContent", pageId);
  await deleteDoc(docRef);
}

export async function updateNavigation(navId: 'headerNav' | 'footerNav', navData: { links: NavLink[] }): Promise<void> {
  const docRef = doc(db, "appContent", navId);
  await setDoc(docRef, navData, { merge: true });
}

export async function updateSocialLinks(links: SocialLinks): Promise<void> {
  const docRef = doc(db, "appContent", "socialLinks");
  await setDoc(docRef, links, { merge: true });
}

// --- Admin: Redirect Management ---

export async function getRedirects(): Promise<Redirect[]> {
  const redirectsCollection = collection(db, "redirects");
  const q = query(redirectsCollection, orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as Redirect));
}

export async function addRedirect(source: string, destination: string): Promise<Redirect> {
  if (!source.startsWith('/')) {
    throw new Error("Source path must start with a '/'.");
  }
  const redirectsCollection = collection(db, "redirects");
  const newRedirectData = { source, destination, createdAt: new Date().toISOString() };
  const docRef = await addDoc(redirectsCollection, newRedirectData);
  return { id: docRef.id, ...newRedirectData };
}

export async function deleteRedirect(id: string): Promise<void> {
  const redirectDoc = doc(db, "redirects", id);
  await deleteDoc(redirectDoc);
}
